import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { colors, typo } from '../../../../theme/tokens';
import { WriteIconButton } from '../../../../components/ui';

interface Props {
  value: string;
  /** Shown (muted) when the value is empty. */
  placeholder?: string;
  /** Render the value in the monospace style (used for the email cell). */
  mono?: boolean;
  disabled?: boolean;
  /** Accessible label, e.g. "display name" / "email". */
  label: string;
  /** Called with the trimmed new value only when it actually changed. */
  onSave: (next: string) => void;
  /** If set, confirm with the user before committing a change. */
  confirmMessage?: string;
}

/**
 * Click-to-edit text cell. Shows the value with a hover edit affordance;
 * clicking turns it into a TextField that commits on Enter/blur and reverts
 * on Escape. Mirrors the inline-edit feel of the Role/Active controls so the
 * admin can edit a user's name or email straight from the table.
 */
const InlineEditCell: React.FC<Props> = ({
  value,
  placeholder = '—',
  mono,
  disabled,
  label,
  onSave,
  confirmMessage,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      setDraft(value);
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [editing, value]);

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next === (value ?? '').trim()) return; // no-op
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    onSave(next);
  };
  const cancel = () => {
    setEditing(false);
    setDraft(value);
  };

  if (editing) {
    return (
      <TextField
        inputRef={inputRef}
        size="small"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        fullWidth
        InputProps={{ sx: mono ? { ...typo.mono, fontSize: 12 } : { fontSize: 13 } }}
      />
    );
  }

  const textSx = mono
    ? { ...typo.mono, fontSize: 12, fontWeight: 600, color: colors.textPrimary }
    : { fontSize: 13, color: colors.textSecondary };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        minWidth: 0,
        '&:hover .inline-edit-aff': { opacity: disabled ? 0 : 0.55 },
      }}
    >
      <Typography
        sx={{ ...textSx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        onDoubleClick={() => !disabled && setEditing(true)}
      >
        {value && value.length ? value : placeholder}
      </Typography>
      {!disabled && (
        <Tooltip title={`Edit ${label}`}>
          <WriteIconButton ignoreEnvReadOnly
            className="inline-edit-aff"
            size="small"
            aria-label={`Edit ${label}`}
            onClick={() => setEditing(true)}
            sx={{ opacity: 0, transition: 'opacity .15s', p: 0.25, flexShrink: 0 }}
          >
            <EditOutlinedIcon sx={{ fontSize: 14 }} />
          </WriteIconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default InlineEditCell;
